import { useEffect, useMemo, useState } from 'react'
import { tournamentApi } from '../services/api'
import { newIdempotencyKey } from '../utils/idempotency'

/**
 * Shared reseller-triggered partial-refund modal. Drives both Stay & Play
 * bookings (kind='booking') and Custom Trip quotes (kind='quote') off the same
 * screen — the only difference is the API base path and how a "payer" is keyed
 * (a captured card row for bookings; a golfer for split-pay quotes).
 *
 * Flow: load the refund context (payers + itinerary + cap), let the reseller
 * pick which payer to refund, optionally remove rooms/golfers that are no
 * longer coming (which auto-suggests a $ amount), then issue the refund up to
 * the allowed cap. The cap is the refundable portion only — non-refundable
 * deposits/hotels are excluded, and each payer is capped at their own share.
 *
 * Props:
 *   kind    'booking' | 'quote'
 *   id      booking id or quote id
 *   onClose ()          — dismiss
 *   onDone  (result)    — called after a successful refund (parent reloads)
 *   t       translation fn (key, fallback, params)
 */
const inp = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }
const lbl = { fontSize: 11.5, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }

function fmtMoney(value, currency) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  const s = '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency ? `${currency} ${s}` : s
}
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100

export default function PartialRefundModal({ kind, id, onClose, onDone, t }) {
  const base = `/portal/reseller/${kind === 'quote' ? 'quotes' : 'bookings'}/${id}/partial-refund`

  const [ctx, setCtx] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  // One idempotency key per modal instance — a double-click / retry of this
  // refund replays the first result instead of issuing a second (guardrail #237).
  const [idemKey] = useState(newIdempotencyKey)
  const [payerId, setPayerId] = useState('')
  const [rooms, setRooms] = useState({})     // { [hotelId]: nRemoved }
  const [golfers, setGolfers] = useState({}) // { [roundId]: nRemoved }
  const [amount, setAmount] = useState('')
  const [amountTouched, setAmountTouched] = useState(false)
  const [reason, setReason] = useState('')

  useEffect(() => {
    let live = true
    tournamentApi.get(`${base}/context`)
      .then(r => { if (!live) return; setCtx(r.data); const p = (r.data.payers || [])[0]; if (p) setPayerId(String(p.id)) })
      .catch(e => { if (live) setErr(e?.response?.data?.message || e.message) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [base])

  const payer = useMemo(() => (ctx?.payers || []).find(p => String(p.id) === String(payerId)) || null, [ctx, payerId])

  // Value of the removed rooms/golfers, capped at the trip's refundable max.
  const suggested = useMemo(() => {
    if (!ctx) return 0
    let sum = 0
    for (const h of ctx.hotels || []) { const n = Number(rooms[h.hotelId] || 0); if (n > 0) sum += h.perRoom * Math.min(n, h.availableRooms) }
    for (const r of ctx.rounds || []) { const n = Number(golfers[r.roundId] || 0); if (n > 0) sum += r.perGolfer * Math.min(n, r.availableGolfers) }
    return round2(Math.min(sum, ctx.maxRefund))
  }, [ctx, rooms, golfers])

  // Per-payer cap: the smaller of what's left on their card and the trip cap.
  const payerCap = useMemo(() => {
    if (!ctx || !payer) return 0
    return round2(Math.min(payer.remaining, ctx.maxRefund))
  }, [ctx, payer])

  // Auto-fill the amount from the edits until the reseller types their own.
  useEffect(() => {
    if (amountTouched) return
    setAmount(suggested > 0 ? String(suggested) : '')
  }, [suggested, amountTouched])

  const amt = round2(amount)
  const overCap = amt > payerCap + 0.005
  // Guardrail #236: over the caller's own ceiling, this refund can still be set
  // up but will be parked for owner approval before any money moves.
  const refundLimit = ctx?.refundLimit ?? null
  const needsApproval = refundLimit != null && amt > refundLimit
  const canSubmit = !!payer && amt > 0 && !overCap && !submitting

  function setRoom(hotelId, max, val) {
    const n = Math.max(0, Math.min(Number(val) || 0, max))
    setRooms(prev => ({ ...prev, [hotelId]: n }))
    setAmountTouched(false)
  }
  function setGolfer(roundId, max, val) {
    const n = Math.max(0, Math.min(Number(val) || 0, max))
    setGolfers(prev => ({ ...prev, [roundId]: n }))
    setAmountTouched(false)
  }

  async function submit() {
    if (!canSubmit) return
    const nm = payer.name || t('pr_this_payer', 'this payer')
    const confirmMsg = needsApproval
      ? t('pr_needs_approval_warn', "Over your {limit} limit — this refund will need owner approval before it's issued.", { limit: fmtMoney(refundLimit, ctx.currency) })
      : t('pr_confirm_start', "Start a refund of {amount} to {name}? We'll email them that it's being processed and finalize it once the hotel/course cancellations clear.", { amount: fmtMoney(amt, ctx.currency), name: nm })
    if (!window.confirm(confirmMsg)) return
    setSubmitting(true); setErr(null)
    const adjustments = {
      rooms: Object.fromEntries(Object.entries(rooms).filter(([, n]) => Number(n) > 0)),
      golfers: Object.fromEntries(Object.entries(golfers).filter(([, n]) => Number(n) > 0)),
    }
    const doPost = (override) => tournamentApi.post(base, { paymentId: payer.id, amount: amt, adjustments, reason: reason || null, override }, { headers: { 'Idempotency-Key': idemKey } })
    try {
      let resp
      try { resp = await doPost(false) }
      catch (e) {
        const code = e?.response?.data?.code
        // Completed / past trip (guardrail #239): owner confirms an override; staff are blocked.
        if (code === 'NEEDS_OVERRIDE') {
          if (!window.confirm(t('pr_override_confirm', 'This trip is already completed or its dates have passed. Issue the refund anyway?'))) return
          resp = await doPost(true)
        } else if (code === 'OWNER_OVERRIDE_REQUIRED') {
          setErr(t('pr_override_owner_only', 'This trip is completed or its dates have passed — only the account owner can refund it.')); return
        } else throw e
      }
      const data = resp.data
      window.alert(data?.awaitingApproval
        ? t('pr_submitted_pending_approval', "Refund set up. It's above your limit, so it was sent to the account owner for approval — the customer won't be refunded until they approve.")
        : t('pr_submitted', "Refund started. The customer has been emailed that it's being processed."))
      onDone && onDone(data)
      onClose && onClose()
    } catch (e) {
      setErr(e?.response?.data?.message || e.message)
    } finally { setSubmitting(false) }
  }

  const cur = ctx?.currency
  const hasItinerary = ctx && ((ctx.hotels || []).length > 0 || (ctx.rounds || []).length > 0)

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 60, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 18, width: '100%', maxWidth: 560, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{t('pr_title', 'Partial refund')}</h3>
          <button onClick={onClose} style={{ border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '6px 12px', cursor: 'pointer' }}>{t('pr_close', 'Close')}</button>
        </div>

        {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{err}</div>}

        {loading ? (
          <div style={{ color: '#64748b', fontSize: 13, padding: 8 }}>{t('pr_loading', 'Loading…')}</div>
        ) : !ctx ? null : (ctx.payers || []).length === 0 || ctx.maxRefund <= 0 ? (
          <div style={{ fontSize: 13, color: '#64748b', padding: 8 }}>
            {t('pr_none_refundable', 'There is nothing left to refund on this trip.')}
            {ctx.nonRefundable > 0 && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>
                {t('pr_nonrefundable_note', '{amount} is non-refundable (deposits / non-refundable hotels) and is excluded from the cap.', { amount: fmtMoney(ctx.nonRefundable, cur) })}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Payer */}
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>{t('pr_payer_label', 'Refund to')}</label>
              {ctx.isSplit ? (
                <select value={payerId} onChange={e => { setPayerId(e.target.value); setAmountTouched(false) }} style={inp}>
                  {ctx.payers.map(p => (
                    <option key={p.id} value={p.id}>
                      {(p.name || t('pr_whole_trip', 'Whole trip')) + (p.isLead ? ' · ' + t('pr_lead', 'lead') : '') + ' — ' + fmtMoney(p.remaining, cur) + ' ' + t('pr_remaining', 'remaining')}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ fontSize: 13, color: '#334155', padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                  {(payer?.name || t('pr_whole_trip', 'Whole trip'))} — {fmtMoney(payer?.remaining || 0, cur)} {t('pr_remaining', 'remaining')}
                </div>
              )}
            </div>

            {/* Itinerary adjusters */}
            {hasItinerary && (
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>{t('pr_adjust_heading', 'Adjust the trip (optional)')}</label>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>{t('pr_adjust_hint', 'Remove rooms or golfers that are no longer coming. The suggested refund updates as you go.')}</div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {(ctx.hotels || []).map(h => (
                    <AdjustRow
                      key={`h${h.hotelId}`}
                      label={h.name}
                      sub={t('pr_rooms_available', '{n} of {total} rooms · {price} each', { n: h.availableRooms, total: h.numRooms, price: fmtMoney(h.perRoom, cur) })}
                      badge={h.refundable ? null : t('pr_nonrefundable_badge', 'non-refundable')}
                      max={h.availableRooms}
                      value={rooms[h.hotelId] || 0}
                      onChange={v => setRoom(h.hotelId, h.availableRooms, v)}
                      removeLabel={t('pr_remove_rooms', 'Remove')}
                    />
                  ))}
                  {(ctx.rounds || []).map(r => (
                    <AdjustRow
                      key={`r${r.roundId}`}
                      label={r.courseName}
                      sub={t('pr_golfers_available', '{n} of {total} golfers · {price} each', { n: r.availableGolfers, total: r.golfers, price: fmtMoney(r.perGolfer, cur) })}
                      badge={null}
                      max={r.availableGolfers}
                      value={golfers[r.roundId] || 0}
                      onChange={v => setGolfer(r.roundId, r.availableGolfers, v)}
                      removeLabel={t('pr_remove_golfers', 'Remove')}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Amount */}
            <div style={{ marginBottom: 6 }}>
              <label style={lbl}>{t('pr_amount_label', 'Refund amount')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number" min="0" step="0.01" value={amount}
                  onChange={e => { setAmount(e.target.value); setAmountTouched(true) }}
                  style={{ ...inp, maxWidth: 180, borderColor: overCap ? '#fca5a5' : '#cbd5e1' }}
                />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{cur}</span>
                {suggested > 0 && !amountTouched && (
                  <span style={{ fontSize: 11.5, color: '#2563eb' }}>{t('pr_suggested', 'suggested from edits')}</span>
                )}
              </div>
            </div>

            <div style={{ fontSize: 12, color: overCap ? '#b91c1c' : '#64748b', marginBottom: 4 }}>
              {overCap
                ? t('pr_over_cap', 'That is more than the {amount} allowed for this payer.', { amount: fmtMoney(payerCap, cur) })
                : t('pr_cap_hint', 'Up to {amount} can be refunded to this payer.', { amount: fmtMoney(payerCap, cur) })}
            </div>
            {needsApproval && !overCap && (
              <div style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px', marginBottom: 4 }}>
                ⚠ {t('pr_needs_approval_warn', "Over your {limit} limit — this refund will need owner approval before it's issued.", { limit: fmtMoney(refundLimit, cur) })}
              </div>
            )}
            {ctx.nonRefundable > 0 && (
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 4 }}>
                {t('pr_nonrefundable_note', '{amount} is non-refundable (deposits / non-refundable hotels) and is excluded from the cap.', { amount: fmtMoney(ctx.nonRefundable, cur) })}
              </div>
            )}
            {ctx.alreadyRefunded > 0 && (
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 4 }}>
                {t('pr_already_refunded', 'Already refunded on this trip: {amount}', { amount: fmtMoney(ctx.alreadyRefunded, cur) })}
              </div>
            )}

            {/* Reason */}
            <div style={{ marginTop: 12, marginBottom: 16 }}>
              <label style={lbl}>{t('pr_reason_label', 'Reason (internal note, optional)')}</label>
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder={t('pr_reason_ph', 'e.g. one golfer dropped out')} style={inp} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={onClose} style={{ border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}>{t('pr_cancel', 'Cancel')}</button>
              <button onClick={submit} disabled={!canSubmit}
                style={{ border: 'none', borderRadius: 6, background: !canSubmit ? '#fca5a5' : (needsApproval ? '#b45309' : '#b91c1c'), color: '#fff', padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: canSubmit ? 'pointer' : 'default' }}>
                {submitting ? t('pr_submitting', 'Refunding…') : (needsApproval ? t('pr_submit_for_approval', 'Send for approval') : t('pr_submit', 'Issue refund'))}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function AdjustRow({ label, sub, badge, max, value, onChange, removeLabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
          {badge && <span style={{ fontSize: 10, fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '1px 6px', borderRadius: 999 }}>{badge}</span>}
        </div>
        <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{sub}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11.5, color: '#64748b' }}>{removeLabel}</span>
        <input type="number" min="0" max={max} value={value}
          onChange={e => onChange(e.target.value)}
          disabled={max <= 0}
          style={{ width: 56, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, textAlign: 'center', opacity: max <= 0 ? 0.5 : 1 }} />
      </div>
    </div>
  )
}
